/* eslint-disable indent */
import React from 'react';
import { DateTime } from 'luxon';
import { makeStyles, Box } from '~/lib/bn-material-ui';
import { BnDateField, BnAutocompleteField } from '~/common';
import { useOrdersDialogStateSelector, useOrdersDialogDispatch } from '../context/orders-dialog-context';
import { isoDateToDayKey } from '../context/orders-dialog-context-utilities';
import { BnToggleSwitch } from '~/common/form-fields/bn-toggle-switch';
import { AddNoteButton, canAddNote } from './orders-dialog-note';
import { useFeedOptions } from '~/feeds/feed-helpers';
import { dayKeyFormat } from '~/orders/dialogs/order-planner-dialog/context/orders-dialog-context-utilities';
import { useMeasurement } from '~/utility/BinSentry-ui-utility';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  formFieldsColumn: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '-5px',
    flexGrow: 1,
  },
  feedTypeRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '.5rem',
    alignItems: 'flex-end',
  },
  feedTypeSelector: {
    flex: 1,
    margin: '0 0 0.125rem 0',
  },
  notesTextField: {
    flexGrow: 1,
    fontStyle: 'italic',
    color: palette.gray[70],
    borderRadius: '4px',
    '&& > div': {
      padding: '.5rem',
      fontSize: '.95rem',
    },
    '& textarea': {
      minHeight: '4rem',
    },
  },
  denseTextField: {
    margin: '-2px 0 0 0',
  },
  subdued: {
    color: palette.gray[60],
  },
  remaining: {
    display: 'none',
  },
  hiddenMdDown: {
    [breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

export function OrdersDialogOrderForm() {
  const styles = useStyles();
  const [
    submitting,
    unsavedChanges,
    selectedOrderEntity,
    createOrEditSirenAction,
    isEditingNote,
  ] = useOrdersDialogStateSelector([
    'submitting',
    'unsavedChanges',
    'selectedOrderEntity',
    'createOrEditSirenAction',
    'isEditingNote',
  ]);

  const dispatch = useOrdersDialogDispatch();
  const orderProperties = selectedOrderEntity?.properties || {};
  const disabled = !createOrEditSirenAction || submitting > 0;
  const medicated = Boolean(
    typeof unsavedChanges?.medicated === 'boolean' ? unsavedChanges?.medicated : orderProperties.medicated,
  );
  const canSetMedicated = createOrEditSirenAction?.fields?.find(field => field.name === 'medicated');
  const noteEntity = selectedOrderEntity?.getSubEntityByRel('https://api.binsentry.com/rel/order-note');

  return (
    <div className={styles.formFieldsColumn}>
      <DeliveryDateSelector />
      <FeedTypeRow />
      {canSetMedicated && (
        <BnToggleSwitch
          label='Medicated Feed'
          formControlClassName={styles.medicatedSwitch}
          checked={medicated}
          disabled={disabled}
          onChange={() => dispatch({
            type: 'set-unsaved-change-data',
            data: { medicated: !medicated },
          })}
        />
      )}
      {canAddNote({ noteEntity, createOrEditSirenAction, isEditingNote }) && <Box className={styles.hiddenMdDown}>
        <AddNoteButton />
      </Box>
      }
    </div>
  );
}

const DeliveryDateSelector = React.memo(function DeliveryDateSelector ( ) {
  const dispatch = useOrdersDialogDispatch();
  const [submitting, isoDateNoon, deliveryDateErrorMessage] = useOrdersDialogStateSelector([
    'submitting',
    'isoDateNoon',
    'errorMessagesByField[\'deliveryDate\']',
  ]);
  const deliveryDate = isoDateNoon;

  return (
    <BnDateField
      label='Date'
      size='small'
      minDate={DateTime.local().startOf('day').toFormat(dayKeyFormat)}
      value={deliveryDate ? isoDateToDayKey(deliveryDate) : ''}
      errorMessage={deliveryDateErrorMessage}
      disabled={submitting > 0}
      onChange={(e) => {
        dispatch({
          type: 'set-unsaved-change-data',
          data: { deliveryDate: e.target.value },
        });
        dispatch({ type: 'select-date', isoDate: e.target.value });
      }}
    />
  );
});

const FeedTypeRow = React.memo(function FeedTypeRow () {
  const styles = useStyles();
  const dispatch = useOrdersDialogDispatch();

  const [
    submitting,
    isoDateNoon,
    unsavedChanges,
    siteEntity,
    selectedOrderEntity,
    createOrEditSirenAction,
    selectedBarnsBins,
    selectedAllocations,
  ] = useOrdersDialogStateSelector([
    'submitting',
    'isoDateNoon',
    'unsavedChanges',
    'siteEntity',
    'selectedOrderEntity',
    'createOrEditSirenAction',
    'selectedBarnsBins',
    'selectedAllocations',
  ]);
  const selectedFeedId = unsavedChanges?.feedId || selectedOrderEntity?.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href;
  const { feedOptions } = useFeedOptions({ organizationId: siteEntity?.getLinkByRel('self').href });
  const orderMeasurement = useMeasurement({ name: 'orderAmount' });

  const selectedFeed = feedOptions.find(item => item.feedId === selectedFeedId);
  const disabled = !createOrEditSirenAction || submitting > 0 || !isoDateNoon;

  const onFeedChange = ({ feedId, bulkDensity }) => {
    const data = { feedId };
    selectedBarnsBins.forEach((binEntity) => {
      const binId = binEntity.getLinkByRel('self').href;
      const actionFieldName = `binAllocations[${binId}]`;
      const selectedAllocationForThisBin = selectedAllocations?.find(allocation => allocation.binId === binId);
      const allocatedVolume = unsavedChanges?.[actionFieldName] || parseFloat(selectedAllocationForThisBin?.volume);

      const currentConvertedVolume = orderMeasurement.convert({
        volume: allocatedVolume,
        bulkDensity: selectedFeed?.bulkDensity,
      });

      const revertedVolume = orderMeasurement.reverse({
        ...(orderMeasurement.metric === 'weight' ? { weight: currentConvertedVolume } : { volume: allocatedVolume }),
        bulkDensity,
      });
      data[actionFieldName] = Number.isFinite(revertedVolume) ? Math.max(revertedVolume, 0) : null;
    });
    dispatch({
      type: 'set-unsaved-change-data',
      data,
    });
  };

  return (
    <div className={styles.feedTypeRow}>
      <BnAutocompleteField
        className={styles.feedTypeSelector}
        label='Feed Type'
        optionLabel='name'
        margin='dense'
        options={feedOptions}
        disableClearable={true}
        disabled={disabled || feedOptions?.length === 0}
        placeholder='Select Feed Type'
        forcePlaceholder={true}
        value={selectedFeed}
        onChange={(feedOption) => onFeedChange(feedOption)}
      />
      <div className={styles.remaining}>
        30 T <span className={styles.subdued}>remaining</span>
        <span>TODO: This is a placeholder for information which will come from consumption plans</span>
      </div>
    </div>
  );
});
